import React, { useEffect, useMemo, useState } from "react";
import { Download, Pencil } from "lucide-react";
import {
  getSuperAdminBookingInvoices,
  getSuperAdminInvoiceSummary,
  getSuperAdminOfflineInvoices,
  getSuperAdminSubscriptionInvoices,
  PaginatedResponse,
  SuperAdminBookingInvoice,
  SuperAdminOfflineInvoice,
  SuperAdminSubscriptionInvoice,
  updateSuperAdminOfflineInvoice,
} from "../../services/superAdminApi";

type InvoiceTab = "subscription" | "bookings" | "offline";

const tabs: Array<{ key: InvoiceTab; label: string }> = [
  { key: "subscription", label: "Subscription Invoices" },
  { key: "bookings", label: "Bookings Invoices" },
  { key: "offline", label: "Offline Invoices" },
];

const cardCls = "bg-white rounded-xl shadow-sm border border-gray-200 p-4";

const currencyText = (value: number, symbol?: string | null) => {
  const formatted = Number(value || 0).toFixed(2);
  return `${symbol || ""}${formatted}`;
};

const statusBadge = (status: string) => {
  const normalized = (status || "").toLowerCase();
  const settled = normalized === "settled";
  const partial = normalized === "partially_paid";
  const badgeClass = settled
    ? "bg-green-100 text-green-700"
    : partial
      ? "bg-yellow-100 text-yellow-700"
      : "bg-orange-100 text-orange-700";

  const label = settled ? "Settled" : partial ? "Partially Paid" : "Pending";
  return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badgeClass}`}>{label}</span>;
};

const SuperAdminInvoices: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InvoiceTab>("subscription");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(0);

  const [subscriptionRows, setSubscriptionRows] = useState<SuperAdminSubscriptionInvoice[]>([]);
  const [bookingRows, setBookingRows] = useState<SuperAdminBookingInvoice[]>([]);
  const [offlineRows, setOfflineRows] = useState<SuperAdminOfflineInvoice[]>([]);

  const [subscriptionPaging, setSubscriptionPaging] = useState<PaginatedResponse<SuperAdminSubscriptionInvoice> | null>(null);
  const [bookingPaging, setBookingPaging] = useState<PaginatedResponse<SuperAdminBookingInvoice> | null>(null);
  const [offlinePaging, setOfflinePaging] = useState<PaginatedResponse<SuperAdminOfflineInvoice> | null>(null);

  const [editRow, setEditRow] = useState<SuperAdminOfflineInvoice | null>(null);
  const [editPaidAmount, setEditPaidAmount] = useState("");
  const [editPaidOn, setEditPaidOn] = useState("");
  const [editStatus, setEditStatus] = useState<"pending" | "settled">("pending");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const meta = await getSuperAdminInvoiceSummary();
        setSummary(meta.subscription_total || 0);
      } catch {
        // Keep listing usable if summary fails.
      }
    };
    loadSummary();
  }, []);

  const fetchData = async (targetTab = activeTab, targetPage = page, targetSearch = search) => {
    try {
      setLoading(true);
      setError("");
      const params = {
        page: targetPage,
        per_page: perPage,
        search: targetSearch || undefined,
      };

      if (targetTab === "subscription") {
        const res = await getSuperAdminSubscriptionInvoices(params);
        setSubscriptionRows(res.data || []);
        setSubscriptionPaging(res);
      } else if (targetTab === "bookings") {
        const res = await getSuperAdminBookingInvoices(params);
        setBookingRows(res.data || []);
        setBookingPaging(res);
      } else {
        const res = await getSuperAdminOfflineInvoices(params);
        setOfflineRows(res.data || []);
        setOfflinePaging(res);
      }

      setPage(targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load invoices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab, 1, "");
    setSearch("");
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, perPage]);

  const paging = useMemo(() => {
    if (activeTab === "subscription") return subscriptionPaging;
    if (activeTab === "bookings") return bookingPaging;
    return offlinePaging;
  }, [activeTab, subscriptionPaging, bookingPaging, offlinePaging]);

  const total = paging?.total ?? 0;
  const currentPage = paging?.current_page ?? 1;
  const lastPage = paging?.last_page ?? 1;

  const openOfflineEditor = (row: SuperAdminOfflineInvoice) => {
    setEditRow(row);
    setEditPaidAmount(String(row.paid_amount ?? 0));
    setEditPaidOn(row.paid_on || new Date().toISOString().slice(0, 10));
    setEditStatus((row.status || "pending").toLowerCase() === "settled" ? "settled" : "pending");
  };

  const pendingPreview = useMemo(() => {
    if (!editRow) return 0;
    const commission = Number(editRow.commission_amount || 0);
    const paid = Number(editPaidAmount || 0);
    const pending = Number((commission - paid).toFixed(2));
    return pending > 0 ? pending : 0;
  }, [editRow, editPaidAmount]);

  const submitOfflineUpdate = async () => {
    if (!editRow) return;
    try {
      setSaving(true);
      await updateSuperAdminOfflineInvoice(editRow.id, {
        paid_amount: Number(editPaidAmount || 0),
        paid_on: editPaidOn,
        status: editStatus,
      });
      setEditRow(null);
      await fetchData("offline", currentPage, search);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to update offline invoice.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Invoices</h1>
        <p className="text-sm text-gray-500">Track subscription, booking and offline invoices exactly like web module.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Subscription Invoices</p>
          <p className="text-2xl font-bold text-[#402B69]">{summary}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Current Tab Records</p>
          <p className="text-2xl font-bold text-blue-600">{total}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Current Page</p>
          <p className="text-2xl font-bold text-green-600">{currentPage}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                    active ? "bg-[#885ABB] text-white" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 border-b bg-white flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]">
            <label className="block text-xs text-gray-500 mb-1">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by company, package, transaction..."
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => fetchData(activeTab, 1, search)}
            className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              fetchData(activeTab, 1, "");
            }}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Reset
          </button>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-6 text-gray-600">Loading invoices...</div>
          ) : error ? (
            <div className="p-6 text-red-600">{error}</div>
          ) : activeTab === "subscription" ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Company</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Package</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Transaction ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Next Payment Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Payment Gateway</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {subscriptionRows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-gray-500" colSpan={9}>No subscription invoices found.</td>
                  </tr>
                ) : (
                  subscriptionRows.map((row, idx) => (
                    <tr key={`${row.method_key}-${row.id}`} className="border-b last:border-b-0 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-gray-700">{(currentPage - 1) * perPage + idx + 1}</td>
                      <td className="px-4 py-3 text-gray-700">{row.company || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.package || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.transaction_id || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{currencyText(row.amount)}</td>
                      <td className="px-4 py-3 text-gray-700">{row.paid_on || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.next_pay_date || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.method || "-"}</td>
                      <td className="px-4 py-3 text-right">
                        {row.download_url ? (
                          <a
                            href={row.download_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"
                            title="Download"
                          >
                            <Download size={14} />
                          </a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : activeTab === "bookings" ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Company</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Transaction ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Application Fee</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Method</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Paid On</th>
                </tr>
              </thead>
              <tbody>
                {bookingRows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-gray-500" colSpan={7}>No booking invoices found.</td>
                  </tr>
                ) : (
                  bookingRows.map((row, idx) => (
                    <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-gray-700">{(currentPage - 1) * perPage + idx + 1}</td>
                      <td className="px-4 py-3 text-gray-700">{row.company || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.transaction_id || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{currencyText(row.amount, row.currency_symbol)}</td>
                      <td className="px-4 py-3 text-gray-700">{currencyText(row.application_fee, row.currency_symbol)}</td>
                      <td className="px-4 py-3 text-gray-700">{row.method || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.paid_on || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Company</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Total Earning</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Commission Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Paid Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Pending Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Paid On</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {offlineRows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-gray-500" colSpan={9}>No offline invoices found.</td>
                  </tr>
                ) : (
                  offlineRows.map((row, idx) => (
                    <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-gray-700">{(currentPage - 1) * perPage + idx + 1}</td>
                      <td className="px-4 py-3 text-gray-700">{row.company || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{currencyText(row.total_earning, row.currency_symbol)}</td>
                      <td className="px-4 py-3 text-gray-700">{currencyText(row.commission_amount, row.currency_symbol)}</td>
                      <td className="px-4 py-3 text-gray-700">{currencyText(row.paid_amount, row.currency_symbol)}</td>
                      <td className="px-4 py-3 text-gray-700">{currencyText(row.pending_amount, row.currency_symbol)}</td>
                      <td className="px-4 py-3 text-gray-700">{row.paid_on || "Not paid"}</td>
                      <td className="px-4 py-3 text-gray-700">{statusBadge(row.status_label)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openOfflineEditor(row)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            Showing page {currentPage} of {lastPage} ({total} records)
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchData(activeTab, currentPage - 1, search)}
              disabled={currentPage <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => fetchData(activeTab, currentPage + 1, search)}
              disabled={currentPage >= lastPage || loading}
              className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editRow ? (
        <div className="fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-[#402B69]">Update Offline Commission</h3>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Company Name</label>
                <input value={editRow.company_name || editRow.company} disabled className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Total Earning</label>
                <input value={currencyText(editRow.total_earning, editRow.currency_symbol)} disabled className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Total Commission Amount</label>
                <input value={currencyText(editRow.commission_amount, editRow.currency_symbol)} disabled className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Pending Amount</label>
                <input value={currencyText(pendingPreview, editRow.currency_symbol)} disabled className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Paid Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editPaidAmount}
                  onChange={(e) => setEditPaidAmount(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Paid On</label>
                <input
                  type="date"
                  value={editPaidOn}
                  onChange={(e) => setEditPaidOn(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as "pending" | "settled")}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="settled">Settled</option>
                </select>
              </div>
            </div>

            <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditRow(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitOfflineUpdate}
                disabled={saving || !editPaidOn}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#885ABB] hover:bg-[#7748A4] disabled:opacity-60"
              >
                {saving ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SuperAdminInvoices;

